/**
 * Daily Log Module TypeScript Interfaces
 * BuilderBel Construction Project Management System
 */

// Daily Log Status - matches backend enum
export type DailyLogStatus = 'draft' | 'submitted' | 'approved' | 'rejected';

// Weather Conditions - matches backend enum
export type WeatherCondition =
  | 'sunny'
  | 'partly_cloudy'
  | 'cloudy'
  | 'rain'
  | 'snow'
  | 'fog'
  | 'windy'
  | 'storm';

// Temperature Unit
export type TemperatureUnit = 'F' | 'C';

// Precipitation Type
export type PrecipitationType = 'none' | 'rain' | 'snow' | 'sleet' | 'hail';

// Equipment Status
export type EquipmentStatus = 'operational' | 'maintenance' | 'idle' | 'breakdown';

// Delay Types
export type DelayType =
  | 'weather'
  | 'material'
  | 'equipment'
  | 'labor'
  | 'inspection'
  | 'design_change'
  | 'permit'
  | 'utility'
  | 'subcontractor'
  | 'owner'
  | 'other';

// Delivery Condition
export type DeliveryCondition = 'good' | 'damaged' | 'partial' | 'rejected';

// Photo Category
export type PhotoCategory = 'progress' | 'safety' | 'delivery' | 'weather' | 'issue' | 'other';

/**
 * Weather Entry Interface
 */
export interface WeatherEntry {
  conditions: WeatherCondition;
  temperatureHigh: number;
  temperatureLow: number;
  temperatureUnit: TemperatureUnit;
  humidity?: number;
  windSpeed?: number;
  precipitation?: number;
  precipitationType: PrecipitationType;
  delayHours: number;
  notes?: string;
}

/**
 * Workforce Entry Interface
 */
export interface WorkforceEntry {
  _id?: string;
  trade: string;
  company: string;
  workerCount: number;
  hours: number;
  workArea?: string;
  supervisor?: string;
  notes?: string;
}

/**
 * Equipment Entry Interface
 */
export interface EquipmentEntry {
  _id?: string;
  type: string;
  equipmentId?: string;
  quantity: number;
  hoursUsed: number;
  operator?: string;
  status: EquipmentStatus;
  notes?: string;
}

/**
 * Work Entry Interface
 */
export interface WorkEntry {
  _id?: string;
  location: string;
  activity: string;
  trade?: string;
  percentComplete?: number;
  startedAt?: string;
  completedAt?: string;
  notes?: string;
}

/**
 * Delay Entry Interface
 */
export interface DelayEntry {
  _id?: string;
  type: DelayType;
  description: string;
  hoursLost: number;
  impactedTrades: string[];
  responsibleParty?: string;
  resolutionPlan?: string;
  isResolved: boolean;
}

/**
 * Visitor Entry Interface
 */
export interface VisitorEntry {
  _id?: string;
  name: string;
  company: string;
  purpose: string;
  timeIn: string;
  timeOut?: string;
  badgeNumber?: string;
  escortedBy?: string;
  notes?: string;
}

/**
 * Delivery Entry Interface
 */
export interface DeliveryEntry {
  _id?: string;
  vendor: string;
  materials: string;
  purchaseOrderId?: string;
  purchaseOrderNumber?: string;
  quantity?: number;
  unit?: string;
  receivedBy: string;
  receivedAt: string;
  condition: DeliveryCondition;
  storageLocation?: string;
  notes?: string;
}

/**
 * Safety Entry Interface
 */
export interface SafetyEntry {
  toolboxTalkConducted: boolean;
  toolboxTalkTopic?: string;
  toolboxTalkAttendees?: number;
  observations: string[];
  hazardsIdentified: string[];
  correctiveActions: string[];
  nearMisses: string[];
  incidentReportIds: string[];
  ppeCompliance: boolean;
  siteInspectionCompleted: boolean;
  notes?: string;
}

/**
 * Photo Entry Interface
 */
export interface DailyLogPhoto {
  _id?: string;
  url: string;
  s3Key: string;
  thumbnailUrl?: string;
  caption?: string;
  location?: string;
  category: PhotoCategory;
  takenAt: string;
  takenBy: string;
  takenByName?: string;
}

/**
 * Signature Entry Interface
 */
export interface SignatureEntry {
  userId: string;
  name: string;
  role: string;
  signature: string;
  signedAt: string;
  ipAddress?: string;
}

/**
 * Main Daily Log Interface - matches backend IDailyLog
 */
export interface DailyLog {
  _id: string;
  projectId: string;
  organizationId: string;
  logDate: string;
  dayNumber: number;
  logNumber: string;

  // Reporter info
  reportedBy: string | { _id: string; name: string; email: string };
  reportedByName: string;
  reportedAt: string;

  // Sections
  weather: WeatherEntry;
  workforce: WorkforceEntry[];
  equipment: EquipmentEntry[];
  workPerformed: WorkEntry[];
  delays: DelayEntry[];
  visitors: VisitorEntry[];
  deliveries: DeliveryEntry[];
  safety?: SafetyEntry;
  photos: DailyLogPhoto[];

  // Calculated totals (computed on save)
  totalWorkers: number;
  totalHours: number;
  totalDelayHours: number;

  // Notes
  generalNotes?: string;
  issuesAndConcerns?: string;
  tomorrowPlan?: string;

  // Workflow
  status: DailyLogStatus;
  submittedAt?: string;
  approvedAt?: string;
  rejectionReason?: string;

  // Signatures
  preparedBy?: SignatureEntry;
  approvedBy?: SignatureEntry;

  // Audit
  createdBy: string | { _id: string; name: string; email: string };
  updatedBy?: string | { _id: string; name: string; email: string };
  deletedAt?: string;
  deletedBy?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Create Daily Log Request
 */
export interface CreateDailyLogRequest {
  logDate: string;
  weather: WeatherEntry;
  workforce?: WorkforceEntry[];
  equipment?: EquipmentEntry[];
  workPerformed?: WorkEntry[];
  delays?: DelayEntry[];
  visitors?: VisitorEntry[];
  deliveries?: DeliveryEntry[];
  safety?: Partial<SafetyEntry>;
  generalNotes?: string;
  issuesAndConcerns?: string;
  tomorrowPlan?: string;
}

/**
 * Update Daily Log Request
 */
export interface UpdateDailyLogRequest {
  logDate?: string;
  weather?: WeatherEntry;
  workforce?: WorkforceEntry[];
  equipment?: EquipmentEntry[];
  workPerformed?: WorkEntry[];
  delays?: DelayEntry[];
  visitors?: VisitorEntry[];
  deliveries?: DeliveryEntry[];
  safety?: Partial<SafetyEntry>;
  generalNotes?: string;
  issuesAndConcerns?: string;
  tomorrowPlan?: string;
}

/**
 * Signature Request
 */
export interface SignatureRequest {
  signature: string;
  ipAddress?: string;
}

/**
 * Reject Request
 */
export interface RejectDailyLogRequest {
  reason: string;
}

/**
 * Add Photos Request
 */
export interface AddPhotosRequest {
  photos: Omit<DailyLogPhoto, '_id'>[];
}

/**
 * List Query Parameters
 */
export interface DailyLogListParams {
  page?: number;
  limit?: number;
  status?: DailyLogStatus | 'all';
  startDate?: string;
  endDate?: string;
  reportedBy?: string;
  search?: string;
  sortBy?: 'logDate' | 'dayNumber' | 'status' | 'createdAt' | 'totalWorkers' | 'totalHours';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Paginated Response
 */
export interface DailyLogListResponse {
  dailyLogs: DailyLog[];
  summary: {
    total: number;
    totalWorkers: number;
    totalHours: number;
    totalDelayHours: number;
    byStatus: Record<DailyLogStatus, number>;
    lastLogDate: string | null;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

/**
 * Daily Log Statistics
 */
export interface DailyLogStats {
  total: number;
  draft: number;
  submitted: number;
  approved: number;
  rejected: number;
  totalWorkers: number;
  totalHours: number;
  totalDelayHours: number;
  averageWorkersPerDay: number;
  daysWithDelays: number;
}

/**
 * Workforce Summary Response
 */
export interface WorkforceSummary {
  byTrade: Array<{
    trade: string;
    totalWorkers: number;
    totalHours: number;
    daysWorked: number;
  }>;
  byCompany: Array<{
    company: string;
    totalWorkers: number;
    totalHours: number;
    daysWorked: number;
  }>;
  byDate: Array<{
    date: string;
    totalWorkers: number;
    totalHours: number;
  }>;
  totals: {
    totalWorkers: number;
    totalHours: number;
    averageWorkersPerDay: number;
  };
}

/**
 * Workforce Summary Query Parameters
 */
export interface WorkforceSummaryParams {
  startDate: string;
  endDate: string;
  groupBy?: 'trade' | 'company' | 'date';
}

/**
 * Delays Summary Response
 */
export interface DelaysSummary {
  byType: Array<{
    type: DelayType;
    totalHours: number;
    count: number;
  }>;
  byDate: Array<{
    date: string;
    totalHours: number;
  }>;
  totals: {
    totalHours: number;
    daysWithDelays: number;
  };
}

/**
 * Delays Summary Query Parameters
 */
export interface DelaysSummaryParams {
  startDate: string;
  endDate: string;
}

/**
 * Calendar Data Response
 */
export interface CalendarData {
  date: string;
  logId: string;
  logNumber: string;
  dayNumber: number;
  status: DailyLogStatus;
  totalWorkers: number;
  hasDelays: boolean;
}

/**
 * Calendar Query Parameters
 */
export interface CalendarParams {
  month?: number;
  year?: number;
}

// ============================================================================
// Helper constants for labels and colors
// ============================================================================

export const DAILY_LOG_STATUS_LABELS: Record<DailyLogStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const DAILY_LOG_STATUS_COLORS: Record<DailyLogStatus, string> = {
  draft: 'gray',
  submitted: 'blue',
  approved: 'green',
  rejected: 'red',
};

export const WEATHER_CONDITION_LABELS: Record<WeatherCondition, string> = {
  sunny: 'Sunny',
  partly_cloudy: 'Partly Cloudy',
  cloudy: 'Cloudy',
  rain: 'Rain',
  snow: 'Snow',
  fog: 'Fog',
  windy: 'Windy',
  storm: 'Storm',
};

export const WEATHER_CONDITION_ICONS: Record<WeatherCondition, string> = {
  sunny: 'sun',
  partly_cloudy: 'cloud-sun',
  cloudy: 'cloud',
  rain: 'cloud-rain',
  snow: 'snowflake',
  fog: 'smog',
  windy: 'wind',
  storm: 'cloud-bolt',
};

export const DELAY_TYPE_LABELS: Record<DelayType, string> = {
  weather: 'Weather',
  material: 'Material',
  equipment: 'Equipment',
  labor: 'Labor',
  inspection: 'Inspection',
  design_change: 'Design Change',
  permit: 'Permit',
  utility: 'Utility',
  subcontractor: 'Subcontractor',
  owner: 'Owner',
  other: 'Other',
};

export const EQUIPMENT_STATUS_LABELS: Record<EquipmentStatus, string> = {
  operational: 'Operational',
  maintenance: 'Maintenance',
  idle: 'Idle',
  breakdown: 'Breakdown',
};

export const DELIVERY_CONDITION_LABELS: Record<DeliveryCondition, string> = {
  good: 'Good',
  damaged: 'Damaged',
  partial: 'Partial',
  rejected: 'Rejected',
};

export const PHOTO_CATEGORY_LABELS: Record<PhotoCategory, string> = {
  progress: 'Progress',
  safety: 'Safety',
  delivery: 'Delivery',
  weather: 'Weather',
  issue: 'Issue',
  other: 'Other',
};

// ============================================================================
// Default values for new entries
// ============================================================================

export const DEFAULT_WEATHER_ENTRY: WeatherEntry = {
  conditions: 'sunny',
  temperatureHigh: 75,
  temperatureLow: 55,
  temperatureUnit: 'F',
  precipitationType: 'none',
  delayHours: 0,
};

export const DEFAULT_SAFETY_ENTRY: SafetyEntry = {
  toolboxTalkConducted: false,
  observations: [],
  hazardsIdentified: [],
  correctiveActions: [],
  nearMisses: [],
  incidentReportIds: [],
  ppeCompliance: true,
  siteInspectionCompleted: false,
};

export const EMPTY_WORKFORCE_ENTRY: Partial<WorkforceEntry> = {
  trade: '',
  company: '',
  workerCount: 0,
  hours: 8,
};

export const EMPTY_EQUIPMENT_ENTRY: Partial<EquipmentEntry> = {
  type: '',
  quantity: 1,
  hoursUsed: 8,
  status: 'operational',
};

export const EMPTY_WORK_ENTRY: Partial<WorkEntry> = {
  location: '',
  activity: '',
};

export const EMPTY_DELAY_ENTRY: Partial<DelayEntry> = {
  type: 'other',
  description: '',
  hoursLost: 0.25,
  impactedTrades: [],
  isResolved: false,
};

export const EMPTY_VISITOR_ENTRY: Partial<VisitorEntry> = {
  name: '',
  company: '',
  purpose: '',
  timeIn: new Date().toISOString(),
};

export const EMPTY_DELIVERY_ENTRY: Partial<DeliveryEntry> = {
  vendor: '',
  materials: '',
  receivedBy: '',
  receivedAt: new Date().toISOString(),
  condition: 'good',
};
